<template>
  <div class="transfer-stock-import">
    <div class="d-flex align-items-center mb-4">
      <h1 class="h3 d-flex align-items-center mb-0">
        <a-button type="link" size="small" class="mr-2" @click="goBackToIndex">
          <a-icon type="left" />
        </a-button>
        {{ $t('transferStock.buttons.importTitle') }}
      </h1>
    </div>

    <a-tabs v-model="activeTab" class="pca-tab-product">
      <a-tab-pane key="upload" :tab="$t('transferStock.buttons.import.uploadTab')">
        <a-card :bordered="false">
          <div style="max-width: 600px">
            <h6 class="font-weight-bold mb-2">
              {{ $t('transferStock.buttons.import.step1Title') }}
            </h6>
            <p class="text-muted mb-3">
              {{ $t('transferStock.buttons.import.step1Desc') }}
            </p>
            <a-button
              type="primary"
              icon="download"
              class="mb-4"
              :loading="isDownloading"
              @click="downloadTemplate"
            >
              {{ $t('transferStock.buttons.import.downloadButton') }}
            </a-button>

            <h6 class="font-weight-bold mb-2">
              {{ $t('transferStock.buttons.import.step2Title') }}
            </h6>
            <p class="text-muted mb-3">
              {{ $t('transferStock.buttons.import.step2Desc') }}
            </p>

            <a-upload-dragger
              name="file"
              :multiple="false"
              :show-upload-list="false"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              :before-upload="beforeUpload"
              :custom-request="dummyRequest"
              :disabled="isUploading || processStatus === 'ONPROGRESS'"
            >
              <p class="ant-upload-drag-icon">
                <a-icon type="inbox" />
              </p>
              <p class="ant-upload-text">
                {{ $t('transferStock.buttons.import.uploadPlaceholder') }}
              </p>
              <p class="ant-upload-hint">
                {{ $t('transferStock.buttons.import.uploadHint') }}
              </p>
            </a-upload-dragger>

            <div
              v-if="file"
              class="mt-3 d-flex align-items-center justify-content-between p-2 border rounded"
            >
              <div class="d-flex align-items-center">
                <a-icon type="file-excel" class="mr-2" style="color: #1890ff" />
                <span>{{ file.name }}</span>
              </div>
              <a-icon
                v-if="!isUploading && processStatus !== 'ONPROGRESS'"
                type="close"
                class="cursor-pointer text-danger"
                @click="removeFile"
              />
            </div>

            <a-button
              v-if="file"
              type="primary"
              class="mt-3 w-100"
              :loading="isUploading || processStatus === 'ONPROGRESS'"
              :disabled="!!processResult"
              @click="handleUpload"
            >
              {{
                isUploading || processStatus === 'ONPROGRESS'
                  ? $t('transferStock.buttons.import.processing')
                  : $t('transferStock.buttons.import.startImport')
              }}
            </a-button>
          </div>

          <div v-if="processResult" class="mt-4 pt-4 border-top">
            <div
              class="p-4 rounded mb-4"
              :style="{
                backgroundColor:
                  processResult.status === 'FAILED' ? '#fff1f0' : '#f6ffed',
                border:
                  processResult.status === 'FAILED'
                    ? '1px solid #ffa39e'
                    : '1px solid #b7eb8f',
              }"
            >
              <div class="d-flex">
                <a-icon
                  :type="
                    processResult.status === 'FAILED'
                      ? 'close-circle'
                      : 'check-circle'
                  "
                  theme="filled"
                  :style="{
                    fontSize: '24px',
                    color:
                      processResult.status === 'FAILED' ? '#f5222d' : '#52c41a',
                    marginRight: '16px',
                    marginTop: '2px',
                  }"
                />
                <div class="flex-grow-1">
                  <h4
                    class="mb-2 font-weight-bold"
                    :style="{
                      color:
                        processResult.status === 'FAILED'
                          ? '#cf1322'
                          : '#389e0d',
                    }"
                  >
                    {{
                      processResult.status === 'FAILED'
                        ? $t('transferStock.buttons.import.failedTitle')
                        : $t('transferStock.buttons.import.successTitle')
                    }}
                  </h4>
                  <div class="d-flex flex-wrap mb-3" style="gap: 24px">
                    <div>
                      <div class="text-muted small">
                        {{ $t('transferStock.buttons.import.totalRows') }}
                      </div>
                      <div class="font-weight-bold" style="font-size: 16px">
                        {{ processResult.totalRows }}
                      </div>
                    </div>
                    <div>
                      <div class="text-muted small">
                        {{ $t('transferStock.buttons.import.successCount') }}
                      </div>
                      <div
                        class="font-weight-bold"
                        style="font-size: 16px; color: #52c41a"
                      >
                        {{ processResult.successCount }}
                      </div>
                    </div>
                    <div>
                      <div class="text-muted small">
                        {{ $t('transferStock.buttons.import.failedCount') }}
                      </div>
                      <div
                        class="font-weight-bold"
                        style="font-size: 16px; color: #f5222d"
                      >
                        {{ processResult.failedCount }}
                      </div>
                    </div>
                  </div>

                  <div v-if="processResult.processId || processId">
                    <a-button
                      type="primary"
                      icon="download"
                      @click="
                        downloadFile(processResult.processId || processId)
                      "
                    >
                      {{ $t('transferStock.buttons.import.downloadProcessFile') }}
                    </a-button>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="processResult.errors && processResult.errors.length">
              <h6 class="text-danger mt-3 mb-2 font-weight-bold">
                <a-icon type="warning" theme="filled" /> {{ $t('transferStock.buttons.import.errorDetail') }} ({{
                  processResult.errors.length
                }}
                {{ $t('transferStock.buttons.import.lines') }})
              </h6>
              <a-table
                style="border: 1px solid #e4e9f0; border-radius: 5px"
                :columns="errorColumns"
                :data-source="processResult.errors"
                :pagination="{ pageSize: 10, showSizeChanger: true, locale: { items_per_page: $t('utils.items_per_page') } }"
                size="middle"
                row-key="rowNumber"
              >
                <template slot="mono" slot-scope="text">
                  <a-tooltip :title="text">
                    <span
                      class="mono-sm text-truncate d-inline-block"
                      style="max-width: 180px; vertical-align: middle"
                    >
                      {{ text }}
                    </span>
                  </a-tooltip>
                </template>
                <template slot="errorMessage" slot-scope="text">
                  <div style="word-break: break-word; line-height: 1.4; padding: 4px 0;">
                    {{ text }}
                  </div>
                </template>
              </a-table>
            </div>

            <div
              v-else-if="
                processResult.status === 'SUCCESS' ||
                  processResult.status === 'FINISHED'
              "
            >
              <div
                v-if="
                  processResult.numberPairs && processResult.numberPairs.length
                "
                class="mt-3"
              >
                <h6 class="mb-2 font-weight-bold">
                  {{ $t('transferStock.buttons.import.noTransfer') }}
                </h6>
                <table class="catalog-list" style="width: 100%">
                  <thead>
                    <tr>
                      <th style="width: 160px">
                        {{ $t('transferStock.buttons.import.noTransfer') }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="pair in processResult.numberPairs"
                      :key="pair.transferNumber"
                    >
                      <td class="mono-sm">
                        <a-tooltip :title="pair.transferNumber">
                          <span class="text-truncate d-inline-block" style="max-width: 150px; vertical-align: middle">
                            {{ pair.transferNumber }}
                          </span>
                        </a-tooltip>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div
                v-else-if="
                  processResult.transferNumbers &&
                    (Array.isArray(processResult.transferNumbers)
                      ? processResult.transferNumbers.length
                      : !!processResult.transferNumbers)
                "
                class="mt-3"
              >
                <h6 class="mb-2 font-weight-bold">
                  {{ $t('transferStock.buttons.import.noTransfer') }}
                </h6>
                <table class="catalog-list" style="width: 100%">
                  <thead>
                    <tr>
                      <th>{{ $t('transferStock.buttons.import.noTransfer') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="tn in Array.isArray(processResult.transferNumbers)
                        ? processResult.transferNumbers
                        : [processResult.transferNumbers]"
                      :key="tn"
                    >
                      <td class="mono-sm">
                        <a-tooltip :title="tn">
                          <span class="text-truncate d-inline-block" style="max-width: 150px; vertical-align: middle">
                            {{ tn }}
                          </span>
                        </a-tooltip>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </a-card>
      </a-tab-pane>

      <a-tab-pane key="history" :tab="$t('transferStock.buttons.import.historyTab')">
        <a-card :bordered="false" class="card-no-padding">
          <div class="d-flex flex-column gap-3">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <a-input-search
                v-model="historySearch"
                :placeholder="$t('transferStock.buttons.search')"
                style="width: 300px"
                enter-button
                @search="onHistorySearch"
              />
            </div>

            <a-table
              style="border: 1px solid #e4e9f0; border-radius: 5px"
              size="middle"
              :columns="historyColumns"
              :data-source="historyData"
              :loading="isHistoryLoading"
              :pagination="false"
              :expand-icon-as-cell="false"
              :expand-icon-column-index="0"
              :expanded-row-keys="expandedRowKeys"
              :custom-row="
                record => ({ on: { click: () => onClickRow(record) } })
              "
              row-key="processId"
              class="data-list"
              @change="handleTableChange"
              @expand="onExpandRow"
            >
              <div
                slot="expandIcon"
                slot-scope="props"
                class="d-flex align-items-center"
              >
                <div>
                  <p class="font-weight-bold mb-0">
                    {{ props.record.fileName || '-' }}
                  </p>
                  <p class="mt-1 mb-0">
                    <span class="text-muted mono-sm mr-2">
                      {{
                        $moment
                          .unix(props.record.startedAt)
                          .format('DD MMM YYYY HH:mm')
                      }}
                    </span>
                  </p>
                </div>
              </div>
              <template slot="name" slot-scope="">
                <span style="display: none" />
              </template>
              <template slot="status" slot-scope="text">
                <a-tag :color="getStatusColor(text)">
                  {{ text }}
                </a-tag>
              </template>
              <template slot="successCount" slot-scope="text">
                <span style="color: #52c41a; font-weight: 600">{{ text }}</span>
              </template>
              <template slot="failedCount" slot-scope="text">
                <span style="color: #f5222d; font-weight: 600">{{ text }}</span>
              </template>
              <template slot="action" slot-scope="text, record">
                <a-button
                  type="link"
                  icon="download"
                  size="small"
                  @click.stop="downloadHistoryFile(record)"
                >
                  {{ $t('transferStock.buttons.import.downloadFile') }}
                </a-button>
              </template>

              <template slot="expandedRowRender" slot-scope="record">
                <div class="px-3 py-2">
                  <div v-if="record.loadingDetail" class="text-center py-3">
                    <a-spin />
                  </div>
                  <div v-else-if="record.detail">
                    <div
                      v-if="record.detail.errors && record.detail.errors.length"
                    >
                      <table class="catalog-list" style="width: 100%">
                        <thead>
                          <tr>
                            <th style="width: 80px">
                              {{ $t('transferStock.buttons.import.row') }}
                            </th>
                            <th style="width: 160px">
                              {{ $t('transferStock.buttons.import.noTransfer') }}
                            </th>
                            <th style="width: 160px">
                              SKU
                            </th>
                            <th>{{ $t('transferStock.buttons.import.errorMessage') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="err in record.detail.errors"
                            :key="err.rowNumber"
                          >
                            <td>{{ err.rowNumber }}</td>
                            <td class="mono-sm">
                              <a-tooltip :title="err.transferNumber">
                                <span class="text-truncate d-inline-block" style="max-width: 150px; vertical-align: middle">
                                  {{ err.transferNumber }}
                                </span>
                              </a-tooltip>
                            </td>
                            <td class="mono-sm">
                              <a-tooltip :title="err.sku">
                                <span class="text-truncate d-inline-block" style="max-width: 150px; vertical-align: middle">
                                  {{ err.sku }}
                                </span>
                              </a-tooltip>
                            </td>
                            <td>
                              <div style="word-break: break-word; line-height: 1.4; padding: 4px 0;">
                                {{ err.errorMessage }}
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div
                      v-else-if="
                        record.detail.status === 'SUCCESS' ||
                          record.detail.status === 'FINISHED'
                      "
                    >
                      <div class="text-success">
                        <a-icon type="check-circle" theme="filled" /> {{ $t('transferStock.buttons.import.allDataSuccess') }}
                      </div>
                      <div
                        v-if="
                          record.detail.numberPairs &&
                            record.detail.numberPairs.length
                        "
                        class="mt-2"
                      >
                        <table class="catalog-list" style="width: 100%">
                          <thead>
                            <tr>
                              <th style="width: 160px">
                                {{ $t('transferStock.buttons.import.noTransfer') }}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="pair in record.detail.numberPairs"
                              :key="pair.transferNumber"
                            >
                              <td class="mono-sm">
                                <a-tooltip :title="pair.transferNumber">
                                  <span class="text-truncate d-inline-block" style="max-width: 150px; vertical-align: middle">
                                    {{ pair.transferNumber }}
                                  </span>
                                </a-tooltip>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div
                        v-else-if="
                          record.detail.transferNumbers &&
                            record.detail.transferNumbers.length
                        "
                        class="mt-2"
                      >
                        <table class="catalog-list" style="width: 100%">
                          <thead>
                            <tr>
                              <th>{{ $t('transferStock.buttons.import.noTransfer') }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="tn in record.detail.transferNumbers"
                              :key="tn"
                            >
                              <td class="mono-sm">
                                <a-tooltip :title="tn">
                                  <span class="text-truncate d-inline-block" style="max-width: 150px; vertical-align: middle">
                                    {{ tn }}
                                  </span>
                                </a-tooltip>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div v-else class="text-muted small">
                      {{ $t('transferStock.buttons.import.noDetail') }}
                    </div>
                  </div>
                </div>
              </template>

              <template slot="footer">
                <a-pagination
                  v-model="pagination.current"
                  style="display: flex; justify-content: flex-end"
                  :page-size="pagination.pageSize"
                  :page-size-options="['10', '20', '50', '100']"
                  :total="pagination.total"
                  :show-total="
                    (total, range) =>
                      $t('utils.pagination_show_total', {
                        perpage: `${range[0]} - ${range[1]}`,
                        total,
                      })
                  "
                  :disabled="isHistoryLoading"
                  show-size-changer
                  :locale="{ items_per_page: $t('utils.items_per_page') }"
                  @change="handleTableChange"
                  @showSizeChange="onShowSizeChange"
                />
              </template>
            </a-table>
          </div>
        </a-card>
      </a-tab-pane>
    </a-tabs>
  </div>
</template>

<script>
import {
  downloadTransferStockTemplate,
  importTransferStock,
  getTransferStockImportStatus,
  getTransferStockImportHistory,
  downloadTransferStockUploadedFile,
} from '@/api/inventory'

export default {
  name: 'TransferStockImport',
  data() {
    return {
      activeTab: 'upload',
      isDownloading: false,
      isUploading: false,
      file: null,
      processId: null,
      processStatus: null,
      processResult: null,
      pollingInterval: null,
      expandedRowKeys: [],

      // History Data
      historyData: [],
      historySearch: '',
      isHistoryLoading: false,
      pagination: {
        current: 1,
        pageSize: 20,
        total: 0,
        showSizeChanger: true,
      },

    }
  },
  computed: {
    historyColumns() {
      return [
        {
          title: this.$t('transferStock.buttons.import.columns.items'),
          dataIndex: 'name',
          width: 280,
          scopedSlots: { customRender: 'name' },
        },
        {
          title: this.$t('transferStock.buttons.import.columns.totalRows'),
          dataIndex: 'totalRows',
          width: 100,
          align: 'right',
        },
        {
          title: this.$t('transferStock.buttons.import.columns.success'),
          dataIndex: 'successCount',
          width: 100,
          align: 'right',
          scopedSlots: { customRender: 'successCount' },
        },
        {
          title: this.$t('transferStock.buttons.import.columns.failed'),
          dataIndex: 'failedCount',
          width: 100,
          align: 'right',
          scopedSlots: { customRender: 'failedCount' },
        },
        {
          title: this.$t('transferStock.buttons.import.columns.status'),
          dataIndex: 'status',
          width: 120,
          align: 'center',
          scopedSlots: { customRender: 'status' },
        },
        {
          title: this.$t('transferStock.buttons.import.columns.action'),
          key: 'action',
          width: 80,
          align: 'center',
          scopedSlots: { customRender: 'action' },
        },
      ]
    },
    errorColumns() {
      return [
        { title: this.$t('transferStock.buttons.import.columns.row'), dataIndex: 'rowNumber', width: 80 },
        {
          title: this.$t('transferStock.buttons.import.columns.noTransfer'),
          dataIndex: 'transferNumber',
          width: 200,
          scopedSlots: { customRender: 'mono' },
        },
        {
          title: this.$t('transferStock.buttons.import.columns.sku'),
          dataIndex: 'sku',
          width: 200,
          scopedSlots: { customRender: 'mono' },
        },
        {
          title: this.$t('transferStock.buttons.import.columns.errorMessage'),
          dataIndex: 'errorMessage',
          width: 500,
          scopedSlots: { customRender: 'errorMessage' },
        },
      ]
    },
    businessId() {
      return this.$store.state.user.restriction_base === 'Warehouse'
        ? Object.keys(this.$store.getters['user/businessListObjectByKey'])[0]
        : this.$route.query.business_id
    },
  },
  watch: {
    activeTab(val) {
      if (val === 'history') {
        this.fetchHistory()
      }
    },
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    goBackToIndex() {
      this.$router.push({
        name: 'inventory.transfer.stock',
        query: this.$route.query,
      })
    },
    async downloadTemplate() {
      this.isDownloading = true
      try {
        const { data } = await downloadTransferStockTemplate({
          business_id: this.businessId,
        })
        const url = window.URL.createObjectURL(new Blob([data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'Template_Transfer_Stock.xlsx')
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (e) {
        this.$notification.error({
          message: this.$t('transferStock.buttons.import.notifications.downloadTemplateError'),
          description:
            e?.message || this.$t('transferStock.buttons.import.notifications.downloadTemplateDesc'),
        })
      } finally {
        this.isDownloading = false
      }
    },
    dummyRequest({ onSuccess }) {
      setTimeout(() => {
        onSuccess('ok')
      }, 0)
    },
    beforeUpload(file) {
      this.file = file
      this.processResult = null
      this.processStatus = null
      return false
    },
    removeFile() {
      this.file = null
      this.processResult = null
      this.processStatus = null
    },
    async handleUpload() {
      if (!this.file) return

      this.isUploading = true
      this.processResult = null

      const formData = new FormData()
      formData.append('file', this.file)

      try {
        const { data } = await importTransferStock({
          business_id: this.businessId,
          data: formData,
        })

        if (data && data.processId) {
          this.processId = data.processId
          this.processStatus = data.status || 'ONPROGRESS'
          this.$message.success(this.$t('transferStock.buttons.import.notifications.uploadSuccess'))
          this.startPolling()
        } else {
          throw new Error('No processId returned')
        }
      } catch (e) {
        this.isUploading = false
        const errorData = e?.response?.data
        const hasErrors = !!errorData?.errors?.length
        if (hasErrors) {
          this.processResult = {
            ...errorData,
            status: errorData?.status || 'FAILED',
          }
          this.$notification.error({
            message: this.$t('transferStock.buttons.import.notifications.uploadError'),
            description: this.$t('transferStock.buttons.import.notifications.uploadErrorData'),
          })
        } else {
          this.$notification.error({
            message: this.$t('transferStock.buttons.import.notifications.uploadError'),
            description:
              errorData?.message ||
              e?.message ||
              this.$t('transferStock.buttons.import.notifications.uploadErrorFile'),
          })
        }
      }
    },
    startPolling() {
      this.stopPolling()
      this.pollingInterval = setInterval(this.checkStatus, 2000)
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },
    async checkStatus() {
      if (!this.processId) return

      try {
        const { data } = await getTransferStockImportStatus(this.processId)
        this.processStatus = data?.status

        if (['FINISHED', 'FAILED', 'SUCCESS'].includes(data?.status)) {
          this.stopPolling()
          this.isUploading = false
          this.processResult = data
          if (data?.status === 'FAILED') {
            this.$notification.error({
              message: this.$t('transferStock.buttons.import.notifications.importFailed'),
              description: this.$t('transferStock.buttons.import.notifications.importFailedDesc'),
            })
          } else {
            this.$notification.success({ message: this.$t('transferStock.buttons.import.notifications.importSuccess') })
          }
        }
      } catch (e) {
        this.stopPolling()
        this.isUploading = false
        console.error('Check status error', e)
      }
    },

    // History Methods
    async fetchHistory() {
      this.isHistoryLoading = true
      this.expandedRowKeys = []
      try {
        const { data } = await getTransferStockImportHistory({
          business_id: this.businessId,
          page: this.pagination.current - 1, // API usually 0-indexed
          size: this.pagination.pageSize,
          search: this.historySearch, // Pass search param
        })

        this.historyData = data.content || []
        this.pagination.total = data.totalElements || 0
      } catch (e) {
        console.error('Fetch history error', e)
        this.historyData = []
      } finally {
        this.isHistoryLoading = false
      }
    },
    onHistorySearch() {
      this.pagination.current = 1
      this.expandedRowKeys = []
      this.fetchHistory()
    },
    handleTableChange(page, pageSize) {
      // Handle both direct call from pagination or from table change event (which passes pagination obj)
      const p = typeof page === 'object' ? page.current : page
      const s = typeof page === 'object' ? page.pageSize : pageSize

      this.pagination.current = p
      if (s) this.pagination.pageSize = s
      this.expandedRowKeys = []
      this.fetchHistory()
    },
    onShowSizeChange(current, size) {
      this.pagination.current = 1
      this.pagination.pageSize = size
      this.expandedRowKeys = []
      this.fetchHistory()
    },
    getStatusColor(status) {
      if (status === 'ONPROGRESS') return 'blue'
      if (status === 'FINISHED' || status === 'SUCCESS') return 'green'
      if (status === 'FAILED') return 'red'
      return 'default'
    },
    async downloadHistoryFile(record) {
      this.downloadFile(record.processId, record.fileName)
    },
    async downloadFile(processId, fileName) {
      if (!processId) return
      try {
        const { data } = await downloadTransferStockUploadedFile(processId)
        const url = window.URL.createObjectURL(new Blob([data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName || `Import_${processId}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (e) {
        this.$notification.error({
          message: this.$t('transferStock.buttons.import.notifications.downloadFileError'),
          description: e?.message || this.$t('transferStock.buttons.import.notifications.downloadFileDesc'),
        })
      }
    },
    onClickRow(record) {
      const key = record.processId
      const isExpanded = this.expandedRowKeys.includes(key)
      if (isExpanded) {
        this.expandedRowKeys = this.expandedRowKeys.filter(k => k !== key)
        return
      }
      if (!record.detail) {
        this.$set(record, 'loadingDetail', true)
      }
      this.expandedRowKeys = [...this.expandedRowKeys, key]
      this.onExpandRow(true, record)
    },
    async onExpandRow(expanded, record) {
      if (expanded && !record.detail) {
        this.$set(record, 'loadingDetail', true)
        try {
          const { data } = await getTransferStockImportStatus(record.processId)
          const tn = data && data.transferNumbers
          const normalizedTN = Array.isArray(tn) ? tn : tn ? [tn] : []
          this.$set(record, 'detail', {
            ...data,
            transferNumbers: normalizedTN,
          })
        } catch (e) {
          this.$notification.error({
            message: this.$t('transferStock.buttons.import.notifications.loadDetailError'),
            description: e?.message || this.$t('transferStock.buttons.import.notifications.loadDetailDesc'),
          })
        } finally {
          this.$set(record, 'loadingDetail', false)
          const hasErrors = !!record?.detail?.errors?.length
          const isSuccess = ['SUCCESS', 'FINISHED'].includes(
            record?.detail?.status,
          )
          const hasTransferNumbers = !!record?.detail?.transferNumbers?.length
          const hasNumberPairs = !!record?.detail?.numberPairs?.length
          const hasContent =
            hasErrors || isSuccess || hasTransferNumbers || hasNumberPairs
          if (!hasContent) {
            this.expandedRowKeys = this.expandedRowKeys.filter(
              k => k !== record.processId,
            )
          }
        }
      }
    },
    onOpen(props, event) {
      props.record.isOpen = !props.record.isOpen
      props.onExpand(props.record, event)
    },
  },
}
</script>

<style lang="scss" scoped>
.transfer-stock-import {
  .cursor-pointer {
    cursor: pointer;
  }
}
.pca-tab-product {
  ::v-deep(.ant-tabs-nav .ant-tabs-tab:hover),
  ::v-deep(.ant-tabs-nav .ant-tabs-tab-active) {
    color: #ff0a54;
  }

  ::v-deep(.ant-tabs-ink-bar) {
    background-color: #ff0a54;
  }
}
.catalog-list {
  border: 1px solid #e4e9f0;
}
.catalog-list tr > th {
  padding: 10px;
  font-weight: 500;
  background-color: #f8f9fa;
}
.catalog-list tr > td {
  padding: 5px 10px;
  background-color: #fff;
}
.catalog-list tr > th,
.catalog-list tr > td {
  border-bottom: 1px solid #e4e9f0;
}
.mono-sm {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    'Liberation Mono', 'Courier New', monospace;
  font-size: 12px;
}
.data-list ::v-deep(.ant-table-tbody > tr) {
  cursor: pointer;
}
.data-list ::v-deep(.ant-table-tbody > tr:hover) {
  background-color: #f8f9fa;
}
.data-list ::v-deep(.ant-table-expanded-row > td) {
  padding: 12px 16px !important;
}
</style>
